import * as Config from '../core/config.js';

const LS_KEY_PREFIX = 'hexLifeExplorer_';
const KEYS = {
    RULESET: `${LS_KEY_PREFIX}ruleset`,
    WORLD_SETTINGS: `${LS_KEY_PREFIX}worldSettings`,
    SIM_SPEED: `${LS_KEY_PREFIX}simSpeed`,
    BRUSH_SIZE: `${LS_KEY_PREFIX}brushSize`,
    RULESET_PANEL_STATE: `${LS_KEY_PREFIX}rulesetPanelState`,
    SETUP_PANEL_STATE: `${LS_KEY_PREFIX}setupPanelState`,
    ANALYSIS_PANEL_STATE: `${LS_KEY_PREFIX}analysisPanelState`,
    RULERANK_PANEL_STATE: `${LS_KEY_PREFIX}ruleRankPanelState`,
    LEARNING_PANEL_STATE: `${LS_KEY_PREFIX}learningPanelState`,
    RULESETACTIONS_PANEL_STATE: `${LS_KEY_PREFIX}rulesetActionsPanelState`,
    WORLDSETUP_PANEL_STATE: `${LS_KEY_PREFIX}worldSetupPanelState`,
    CHROMALAB_PANEL_STATE: `${LS_KEY_PREFIX}chromaLabPanelState`,
    SHORTCUTS_PANEL_STATE: `${LS_KEY_PREFIX}shortcutsPanelState`,
    EXPLORE_PANEL_STATE: `${LS_KEY_PREFIX}explorePanelState`,
    UI_SETTINGS: `${LS_KEY_PREFIX}uiSettings`,
    USER_RULESETS: `${LS_KEY_PREFIX}userRulesets`,
    USER_PATTERNS: `${LS_KEY_PREFIX}userPatterns`,
    EXPLORE_GALLERY: `${LS_KEY_PREFIX}exploreGallery`,
    EMBEDDING_GALLERY: `${LS_KEY_PREFIX}embeddingGallery`,
    NATIVE_DESCRIPTOR_GALLERY: `${LS_KEY_PREFIX}nativeDescriptorGallery`,
    FAB_SETTINGS: `${LS_KEY_PREFIX}fabSettings`,
    ONBOARDING_STATES: `${LS_KEY_PREFIX}onboardingStates`,
    COLOR_SETTINGS: `${LS_KEY_PREFIX}colorSettings`,
    PUBLIC_THUMB_CACHE: `${LS_KEY_PREFIX}publicThumbCache`,
    INTERESTINGNESS_VOTES: `${LS_KEY_PREFIX}interestingnessVotes`,
    SAVED_STATES: `${LS_KEY_PREFIX}savedStates`,
    PREDICTION_RESULTS: `${LS_KEY_PREFIX}predictionResults`,
};

/** FIFO cap on banked prediction rounds — the labels are the asset, not the transcript. */
const PREDICTION_RESULTS_MAX = 500;

function _getItem(key) {
    try {
        const value = localStorage.getItem(key);
        return value ? JSON.parse(value) : null;
    } catch (e) {
        console.error(`Error getting item ${key} from localStorage:`, e);
        return null;
    }
}

function _setItem(key, value) {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (e) {
        console.error(`Error setting item ${key} in localStorage:`, e);
    }
}

// --- MODIFICATION START ---
// Helper function to migrate old color settings format to the new dual-color format.
function migrateColorGroups(colorGroups) {
    if (!colorGroups || typeof colorGroups !== 'object') {
        return {};
    }
    const migratedGroups = {};
    for (const key in colorGroups) {
        const value = colorGroups[key];
        if (typeof value === 'string') {
            // This is the old format. Convert it.
            // Use the old color for the 'on' state and a default dark color for 'off'.
            migratedGroups[key] = { on: value, off: '#333333' };
        } else if (typeof value === 'object' && value !== null && value.on && value.off) {
            // This is already the new format. Keep it.
            migratedGroups[key] = value;
        }
    }
    return migratedGroups;
}
// --- MODIFICATION END ---


export function loadRuleset() {
    return _getItem(KEYS.RULESET) || Config.INITIAL_RULESET_CODE;
}

export function saveRuleset(rulesetHex) {
    _setItem(KEYS.RULESET, rulesetHex);
}

export function loadWorldSettings() {
    const loaded = _getItem(KEYS.WORLD_SETTINGS);
    if (loaded && Array.isArray(loaded) && loaded.length === Config.NUM_WORLDS) {
        // --- MODIFICATION START ---
        const isValid = loaded.every(s =>
            (typeof s.initialDensity === 'number' || typeof s.initialState === 'object') &&
            typeof s.enabled === 'boolean' &&
            (typeof s.rulesetHex === 'string' && /^[0-9a-fA-F]{32}$/.test(s.rulesetHex))
        );

        if (isValid) {
            return loaded.map(s => {
                if (s.initialState) {
                    return s; // Already in new format
                }
                // Migration for old format
                return {
                    ...s,
                    initialState: {
                        mode: 'density',
                        params: { density: s.initialDensity }
                    }
                };
            });
        }
        // --- MODIFICATION END ---
        console.warn("Loaded world settings format error or missing rulesetHex, reverting to defaults.");
    }

    const defaultSettings = [];
    const defaultRuleset = loadRuleset() || "0".repeat(32);
    for (let i = 0; i < Config.NUM_WORLDS; i++) {
        defaultSettings.push({
            // --- MODIFICATION START ---
            initialState: {
                mode: 'density',
                params: { density: Config.DEFAULT_INITIAL_DENSITIES[i] ?? 0.5 }
            },
            // --- MODIFICATION END ---
            enabled: Config.DEFAULT_WORLD_ENABLED_STATES[i] ?? true,
            rulesetHex: defaultRuleset
        });
    }
    return defaultSettings;
}

export function saveWorldSettings(worldSettingsArray) {
    if (Array.isArray(worldSettingsArray) && worldSettingsArray.length === Config.NUM_WORLDS) {
        // --- MODIFICATION START ---
        const isValid = worldSettingsArray.every(s =>
            typeof s.initialState === 'object' &&
            typeof s.enabled === 'boolean' &&
            (typeof s.rulesetHex === 'string' && /^[0-9a-fA-F]{32}$/.test(s.rulesetHex))
        );
        if (isValid) {
            _setItem(KEYS.WORLD_SETTINGS, worldSettingsArray);
        } else {
            console.error("Attempted to save invalid world settings array format (incl. initialState).");
        }
        // --- MODIFICATION END ---
    } else {
        console.error("Attempted to save world settings array with incorrect length or type.");
    }
}

export function loadSimSpeed() {
    const speed = _getItem(KEYS.SIM_SPEED);
    return typeof speed === 'number' ? speed : Config.DEFAULT_SPEED;
}
export function saveSimSpeed(speed) {
    _setItem(KEYS.SIM_SPEED, speed);
}

export function loadBrushSize() {
    const size = _getItem(KEYS.BRUSH_SIZE);
    return typeof size === 'number' ? size : Config.DEFAULT_NEIGHBORHOOD_SIZE;
}
export function saveBrushSize(size) {
    _setItem(KEYS.BRUSH_SIZE, size);
}

/* Panels registered before the generic scheme keep their historical storage keys (listed in
   KEYS); anything newer falls back to a derived key, so a new panel persists without a KEYS edit. */
function _panelStateKey(panelKey) {
    return KEYS[`${panelKey.toUpperCase()}_PANEL_STATE`] || `${LS_KEY_PREFIX}${panelKey}PanelState`;
}

const _px = (value) => (typeof value === 'string' && value.endsWith('px')) ? value : null;

export function loadPanelState(panelKey) {
    const state = _getItem(_panelStateKey(panelKey));
    if (state && typeof state.isOpen === 'boolean') {
        return {
            isOpen: state.isOpen,
            x: typeof state.x === 'string' ? state.x : null,
            y: typeof state.y === 'string' ? state.y : null,
            w: _px(state.w),
            h: _px(state.h),
        };
    }
    return { isOpen: false, x: null, y: null, w: null, h: null };
}

export function savePanelState(panelKey, state) {
    if (!state || typeof state.isOpen !== 'boolean') {
        console.warn(`PersistenceService: Invalid state provided for panel "${panelKey}".`);
        return;
    }
    _setItem(_panelStateKey(panelKey), {
        isOpen: state.isOpen,
        x: _px(state.x),
        y: _px(state.y),
        w: _px(state.w),
        h: _px(state.h),
    });
}

export function loadUISetting(settingKey, defaultValue) {
    const allUISettings = _getItem(KEYS.UI_SETTINGS) || {};
    if (Object.prototype.hasOwnProperty.call(allUISettings, settingKey) && allUISettings[settingKey] !== undefined) {
        return allUISettings[settingKey];
    }
    return defaultValue;
}

export function saveUISetting(settingKey, value) {
    const allUISettings = _getItem(KEYS.UI_SETTINGS) || {};
    allUISettings[settingKey] = value;
    _setItem(KEYS.UI_SETTINGS, allUISettings);
}

export function clearAllAppSettings() {
    console.log("Clearing all HexLife Explorer application settings from localStorage...");
    Object.values(KEYS).forEach(key => {
        try {
            localStorage.removeItem(key);
            console.log(`Removed ${key}`);
        } catch (e) {
            console.error(`Error removing item ${key} from localStorage:`, e);
        }
    });
    console.log("All application settings cleared.");
}

export function loadOnboardingStates() {
    return _getItem(KEYS.ONBOARDING_STATES) || {};
}

export function saveOnboardingStates(statesObject) {
    _setItem(KEYS.ONBOARDING_STATES, statesObject);
}

export function loadUserRulesets() {
    return _getItem(KEYS.USER_RULESETS) || [];
}

export function saveUserRulesets(userRulesets) {
    _setItem(KEYS.USER_RULESETS, userRulesets);
}

export function loadUserPatterns() {
    return _getItem(KEYS.USER_PATTERNS) || [];
}

export function saveUserPatterns(userPatterns) {
    _setItem(KEYS.USER_PATTERNS, userPatterns);
}

// Auto-explore session gallery (Phase 4): the MAP-Elites-lite archive's entry list, best-first.
export function loadExploreGallery() {
    return _getItem(KEYS.EXPLORE_GALLERY) || [];
}

export function saveExploreGallery(entries) {
    try {
        localStorage.setItem(KEYS.EXPLORE_GALLERY, JSON.stringify(entries));
    } catch (e) {
        // Thumbnails (v2.6) are the only large field; if the write overflows the quota, retry once
        // with every `thumb` stripped so the gallery data itself is never lost to a full store.
        if (e && (e.name === 'QuotaExceededError' || e.code === 22 || e.code === 1014)) {
            console.warn('saveExploreGallery: quota exceeded, retrying without thumbnails.');
            try {
                const stripped = (entries || []).map(({ thumb: _thumb, ...rest }) => rest);
                localStorage.setItem(KEYS.EXPLORE_GALLERY, JSON.stringify(stripped));
                return;
            } catch (e2) {
                console.error('saveExploreGallery: still failing after stripping thumbnails:', e2);
                return;
            }
        }
        console.error('Error saving explore gallery to localStorage:', e);
    }
}

/**
 * Load the native descriptor archive. Descriptor cells are model-specific, so a model mismatch
 * starts a fresh archive. The obsolete CLIP-keyed blob is discarded during this migration.
 * @param {string|null} [expectedModelId]
 */
export function loadNativeDescriptorGallery(expectedModelId = null) {
    try {
        localStorage.removeItem(KEYS.EMBEDDING_GALLERY);
    } catch { /* storage may be unavailable */ }
    const blob = _getItem(KEYS.NATIVE_DESCRIPTOR_GALLERY);
    if (!blob || !Array.isArray(blob.entries)) return [];
    if (expectedModelId && blob.modelId !== expectedModelId) return [];
    return blob.entries;
}

/** @param {object[]} entries @param {string|null} [modelId] */
export function saveNativeDescriptorGallery(entries, modelId = null) {
    _setItem(KEYS.NATIVE_DESCRIPTOR_GALLERY, { modelId: modelId || null, entries });
}

// Client-side cache of evolved-world thumbnails for PUBLIC library rulesets (keyed by ruleset hex).
// Public rulesets that carry a paired initial condition get their thumbnail baked on demand and cached
// here, so the committed library JSON stays small (it stores only the IC choice, not the image).
// `__v` versions the bake pipeline: v2 = fixed monochrome thumbnail LUT. A version mismatch discards
// the whole cache so palette-dependent v1 thumbs rebake instead of mixing looks with new mono ones.
const PUBLIC_THUMB_CACHE_VERSION = 2;

export function loadPublicThumbCache() {
    const cache = _getItem(KEYS.PUBLIC_THUMB_CACHE) || {};
    if (cache.__v !== PUBLIC_THUMB_CACHE_VERSION) return {};
    const thumbs = { ...cache };
    delete thumbs.__v;
    return thumbs;
}

export function savePublicThumb(hex, thumb) {
    if (!hex || !thumb) return;
    const cache = loadPublicThumbCache();
    cache.__v = PUBLIC_THUMB_CACHE_VERSION;
    cache[hex] = thumb;
    try {
        localStorage.setItem(KEYS.PUBLIC_THUMB_CACHE, JSON.stringify(cache));
    } catch (e) {
        // Thumbnails are the only large field; on quota overflow drop the cache rather than lose data.
        if (e && (e.name === 'QuotaExceededError' || e.code === 22 || e.code === 1014)) {
            console.warn('savePublicThumb: quota exceeded, clearing the public thumbnail cache.');
            try { localStorage.removeItem(KEYS.PUBLIC_THUMB_CACHE); } catch { /* ignore */ }
            return;
        }
        console.error('Error saving public thumbnail cache:', e);
    }
}

export function loadColorSettings() {
    const defaults = {
        mode: 'preset',
        activePreset: 'monochrome',
        customGradient: {
            on: ['#3cb44b', '#ffe119'],
            off: ['#1a4a23', '#665a0a']
        },
        customNeighborColors: Config.DEFAULT_COLOR_SCHEMES.customNeighborColors,
        customSymmetryColors: Config.DEFAULT_COLOR_SCHEMES.customSymmetryColors,
        flickerProofPresets: true,
        // Global hue rotation (degrees) applied to every palette at LUT-generation time.
        hueShift: 0
    };
    const loaded = _getItem(KEYS.COLOR_SETTINGS);
    
    if (loaded) {
        // Create a new object to ensure we don't mutate the defaults
        const migratedSettings = { ...defaults, ...loaded };

        // Run the migration function on the loaded custom color groups
        migratedSettings.customNeighborColors = migrateColorGroups(loaded.customNeighborColors);
        migratedSettings.customSymmetryColors = migrateColorGroups(loaded.customSymmetryColors);

        // Ensure gradient has the correct structure
        if (!migratedSettings.customGradient || !migratedSettings.customGradient.on) {
            migratedSettings.customGradient = defaults.customGradient;
        }
        
        // Ensure flickerProofPresets
        if (typeof loaded.flickerProofPresets !== 'boolean') {
            migratedSettings.flickerProofPresets = defaults.flickerProofPresets;
        }
        
        return migratedSettings;
    }
    
    return defaults;
}


export function saveColorSettings(settings) {
    _setItem(KEYS.COLOR_SETTINGS, settings);
}

// Swipe-to-judge vote bank (PLAY-LAYER-PLAN §S1): append-only pairwise "which is more interesting?"
// votes. Feeds the opt-in weight refit (§S3). Bounded (FIFO cap in VoteBank) so localStorage stays
// small. Stored as a plain array of vote records; a malformed blob loads as empty.
export function loadInterestingnessVotes() {
    const votes = _getItem(KEYS.INTERESTINGNESS_VOTES);
    return Array.isArray(votes) ? votes : [];
}

export function saveInterestingnessVotes(votes) {
    _setItem(KEYS.INTERESTINGNESS_VOTES, Array.isArray(votes) ? votes : []);
}

// Saved Starts: the library of captured initial states (StateLibraryService owns the CRUD).
// Each entry carries its own bit-packed cell payload plus the dims it was captured at; a malformed
// blob — or an entry missing the fields a reset would need — loads as empty rather than poisoning
// a world's initial state.
export function loadSavedStates() {
    const entries = _getItem(KEYS.SAVED_STATES);
    if (!Array.isArray(entries)) return [];
    return entries.filter(e =>
        e && typeof e === 'object' &&
        typeof e.id === 'string' &&
        typeof e.stateB64 === 'string' &&
        Number.isFinite(e.rows) && Number.isFinite(e.cols)
    );
}

/**
 * Persist the saved-starts library.
 * @param {object[]} entries
 * @returns {boolean} False when the write failed (e.g. localStorage quota) — the caller toasts.
 */
export function saveSavedStates(entries) {
    try {
        localStorage.setItem(KEYS.SAVED_STATES, JSON.stringify(Array.isArray(entries) ? entries : []));
        return true;
    } catch (e) {
        console.error('Error saving the saved-starts library to localStorage:', e);
        return false;
    }
}

// Prediction mode (PLAY-LAYER-PLAN §P1, roadmap #19): one record per graded round —
// `{roundSeed, hex, icLabel, predicted, actual, correct, at}`. Two jobs, in this order of importance:
//   1. The *labels*. Every round is a human saying "this ruleset does X" about a real world, which is
//      training data the statistical objective cannot generate for itself (roadmap #37 Stage 4 wants
//      exactly this shape of judgement). That is why the ruleset hex and the round seed are stored and
//      not just a running tally — a record without its world is not a label.
//   2. The player's own streak/accuracy readout, which is display-only and never gates anything.
// Append-only with a FIFO cap; a malformed blob loads as empty rather than poisoning the deck.
export function loadPredictionResults() {
    const rows = _getItem(KEYS.PREDICTION_RESULTS);
    if (!Array.isArray(rows)) return [];
    return rows.filter(r =>
        r && typeof r === 'object' &&
        typeof r.hex === 'string' &&
        typeof r.predicted === 'string' &&
        typeof r.actual === 'string'
    );
}

/**
 * Persist the prediction-round log (trimmed to the newest {@link PREDICTION_RESULTS_MAX}).
 * @param {object[]} rows
 * @returns {boolean} False when the write failed (e.g. localStorage quota).
 */
export function savePredictionResults(rows) {
    const list = Array.isArray(rows) ? rows.slice(-PREDICTION_RESULTS_MAX) : [];
    try {
        localStorage.setItem(KEYS.PREDICTION_RESULTS, JSON.stringify(list));
        return true;
    } catch (e) {
        console.error('Error saving prediction results to localStorage:', e);
        return false;
    }
}
